/**
 * Message List Component
 * Displays list of messages with auto-scroll
 * Matches main app's message list behavior (React Native)
 */

import { ActivityIndicator, FlatList, Icon, StyleSheet, Text, useTheme, View } from '@/components/common';
import { useEffect, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import type { MessageListProps } from '../../types/chat';
import { MessageBubble } from './MessageBubble';
import { useChatStore } from '../../hooks/useChatStore';

export function MessageList({ 
  messages, 
  isLoading = false,
  isTyping = false,
  showSenderNames = false,
  bottomInset = 0,
}: MessageListProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const currentUserId = useChatStore(state => String(state.authContext?.user?.id || state.authContext?.user?.user_id || ''));
  const [typingPhase, setTypingPhase] = useState(0);

  useEffect(() => {
    if (!isTyping) {
      setTypingPhase(0);
      return;
    }

    const interval = setInterval(() => {
      setTypingPhase((prev) => (prev + 1) % 3);
    }, 350);

    return () => clearInterval(interval);
  }, [isTyping]);

  // Handle empty state
  if (messages.length === 0 && !isLoading) {
    return (
      <View style={styles.emptyContainer}>
        <View
          style={[
            styles.emptyIcon,
            {
              backgroundColor: theme.colors.primaryContainer,
              borderColor: theme.colors.outlineVariant,
            },
          ]}
          accessible
          accessibilityRole="image"
          accessibilityLabel={t('chat.empty.iconLabel', 'XEdu assistant')}
        >
          <Icon source="school-outline" size={30} color={theme.colors.primary} />
        </View>
        <Text variant="titleMedium" style={styles.emptyTitle}>
          {t('chat.empty.title', 'XEdu AI is ready')}
        </Text>
        <Text variant="bodyMedium" style={styles.emptyDescription}>
          {t(
            'chat.empty.description',
            'Ask about your child’s application, school messages, classes, or the next step in a school workflow.'
          )}
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <FlatList
        data={messages}
        keyExtractor={(item) => item.id}
        style={styles.list}
        renderItem={({ item }) => (
          <MessageBubble
            message={item}
            isOwn={item.role === 'user' && (!item.metadata?.senderId || item.metadata.senderId === 'you' || item.metadata.senderId === currentUserId)}
            showTimestamp={true}
            showSenderName={showSenderNames}
          />
        )}
        contentContainerStyle={[styles.listContent, bottomInset ? { paddingBottom: bottomInset } : null]}
        inverted={false}
        ListFooterComponent={() => (
          <>
            {/* Typing indicator */}
            {isTyping && (
              <View style={styles.typingContainer}>
                <View
                  style={[
                    styles.typingBubble,
                    {
                      backgroundColor: theme.colors.surface,
                      borderColor: theme.colors.outlineVariant,
                      borderWidth: 1,
                    },
                  ]}
                >
                  <View style={styles.typingDots}>
                    <View
                      style={[
                        styles.dot,
                        {
                          backgroundColor: theme.colors.onSurfaceVariant,
                          opacity: typingPhase === 0 ? 1 : 0.35,
                        },
                      ]}
                    />
                    <View
                      style={[
                        styles.dot,
                        {
                          backgroundColor: theme.colors.onSurfaceVariant,
                          opacity: typingPhase === 1 ? 1 : 0.35,
                        },
                      ]}
                    />
                    <View
                      style={[
                        styles.dot,
                        {
                          backgroundColor: theme.colors.onSurfaceVariant,
                          opacity: typingPhase === 2 ? 1 : 0.35,
                        },
                      ]}
                    />
                  </View>
                </View>
              </View>
            )}

            {/* Loading indicator */}
            {isLoading && !isTyping && (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="small" color={theme.colors.primary} />
                <Text variant="bodySmall" style={[styles.loadingText, { color: theme.colors.onSurfaceVariant }]}>
                  {t('chat.loading.generating', 'Working on it')}
                </Text>
              </View>
            )}
          </>
        )}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  list: {
    flex: 1,
  },
  listContent: {
    paddingTop: 8,
    paddingBottom: 2,
    paddingLeft: 8,
    paddingRight: 12,
    flexGrow: 1,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  emptyIcon: {
    width: 56,
    height: 56,
    borderRadius: 16,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  emptyTitle: {
    marginBottom: 8,
    textAlign: 'center',
    fontWeight: '600',
    fontSize: 17,
    lineHeight: 24,
  },
  emptyDescription: {
    textAlign: 'center',
    opacity: 0.82,
    maxWidth: 360,
    fontSize: 15,
    lineHeight: 23,
  },
  typingContainer: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
    paddingLeft: 8,
    paddingRight: 12,
  },
  typingBubble: {
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  typingDots: {
    flexDirection: 'row',
    gap: 4,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  loadingContainer: {
    padding: 16,
    alignItems: 'center',
    gap: 8,
  },
  loadingText: {
    fontSize: 14,
    lineHeight: 20,
  },
});
